import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_BASE_URL from "../../apis/api";
import {API_INTG_URL} from "../../apis/api"; 

const api = axios.create({
  baseURL: API_INTG_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ Axios interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn("⚠️ Access token expired. Attempting to refresh...");
      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) throw new Error("No refresh token available");
        const response = await api.post("/api/auth/token/refresh/", {
          refresh: refreshToken,
        });
        const { access } = response.data;
        localStorage.setItem("access_token", access);
        setAuthToken(access);
        // Retry the failed request with the new token
        error.config.headers["Authorization"] = `Bearer ${access}`;
        return api.request(error.config);
      } catch (refreshError) {
        console.error("❌ Token refresh failed. Logging out...");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/"; // Redirect to login
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// ✅ Function to set the Authorization token
export const setAuthToken = (token) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  };


// ✅ Create async thunk for fetching case logs
export const fetchCaseLogs = createAsyncThunk(
  "caseLogs/fetchCaseLogs", // Action type prefix
  async (caseNumber, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token)
        throw new Error("No authentication token found. Please log in.");
      setAuthToken(token);
      const response = await api.get(`/api/supplychain/cases/${caseNumber}/`);
      console.log("✅ Case detail fetched:", response.data);
      return { case_number: caseNumber, data: response.data };
    } catch (error) {
      console.error(
        "❌ Error fetching case detail:",
        error.response?.data || error.message
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ✅ Create async thunk for fetching shipment history
export const fetchShipmentHistory = createAsyncThunk(
  "caseLogs/fetchShipmentHistory", // Action type prefix
  async (caseNumber, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token)
        throw new Error("No authentication token found. Please log in.");
      setAuthToken(token);
      const response = await api.get(
        `/api/supplychain/cases/${caseNumber}/shipments/history/`
      );
      console.log("✅ Shipment history fetched:", response.data);
      return { case_number: caseNumber, data: response.data };
    } catch (error) {
      console.error(
        "❌ Error fetching shipment history:",
        error.response?.data || error.message
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const caseLogsSlice = createSlice({
  name: "caseLogs",
  initialState: {
    logs: {}, // Logs keyed by threadId
    shipmentHistory:{},
    status: "idle", // Status: idle, loading, succeeded, failed
    error: null, // Error message
  },
  reducers: {
    clearLogs(state) {
      state.logs = {}; // Clear all logs
      state.shipmentHistory = {}; // Clear shipment history
      state.status = "idle"; // Reset status
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseLogs.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCaseLogs.fulfilled, (state, action) => {
        const { case_number, data } = action.payload;
        state.logs[case_number] = data; // Save logs under the threadId key
        state.status = "succeeded";
      })
      .addCase(fetchCaseLogs.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch case logs.";
      })
       // ✅ Shipment History Reducers
       .addCase(fetchShipmentHistory.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchShipmentHistory.fulfilled, (state, action) => {
        const { case_number, data } = action.payload;
        state.shipmentHistory[case_number] = data; // Store shipment history under caseNumber key
        state.status = "succeeded";
      })
      .addCase(fetchShipmentHistory.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch shipment history.";
      });
  },
});

export const { clearLogs } = caseLogsSlice.actions;
export default caseLogsSlice.reducer;
